import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useCurrentPatient } from '@/hooks/useCurrentPatient';
import { useGreetingNow } from '@/hooks/useGreetingNow';
import { usePortalAppointments, type PortalAppointmentItem } from '@/hooks/usePortalAppointments';
import { usePortalMessages } from '@/hooks/usePortalMessages';
import { usePortalDocuments } from '@/hooks/usePortalDocuments';
import { usePortalShareableLabReports } from '@/hooks/usePortalShareableLabReports';
import { usePortalInvoices } from '@/hooks/usePortalInvoices';
import { usePortalPunchCards } from '@/hooks/usePortalPunchCards';
import {
  usePatientFormsAssignments,
  useRequestPortalFormLink,
  type PatientFormsAssignment,
} from '@/hooks/usePatientFormsAssignments';
import { usePortalTreatmentCourses } from '@/hooks/usePortalTreatmentCourses';
import { usePortalBookingHref } from '@/hooks/usePortalBookingHref';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';
import { isCollectableInvoiceStatus } from '@/lib/invoiceDetailActions';
import { publicUrlFromAssignmentMetadata } from '@/lib/formsUrls';
import { assignmentQuestionnaireName } from '@/lib/questionnaireDisplayName';
import { useQuestionnaireNameMap } from '@/hooks/useQuestionnaireNameMap';
import {
  useCreatePayCheckout,
  buildPayCheckoutReturnUrls,
  payReturnStorageKey,
} from '@/hooks/useClinicPay';
import { markPortalPaymentPending } from '@/lib/portalPaymentSettlement';
import { portalCourseForloebProgress } from '@/lib/courseStepSlotPicker';
import { greetingPeriod } from '@/utils/greeting';
import {
  isUnreadClinicMessage,
  nextUpcomingAppointment,
} from '@/components/portal/dashboard/portalDashboardUtils';
import { PortalBookAppointmentButton } from '@/components/portal/PortalBookAppointmentButton';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import {
  PortalCancelModal,
  PortalRescheduleModal,
} from '@/components/portal/PortalAppointmentModals';
import {
  formatClinicAddress,
  formatPortalDateTime,
  formatPortalKroner,
  formatPortalLongDate,
  formatPortalShortDate,
  formatRelativeDays,
  greetingDa,
} from '@/lib/portalFormat';
import { formatCourseTimeRange } from '@/lib/courseDateFormat';
import {
  appointmentRuleText,
  paymentChip,
  portalAppointmentHostLabel,
} from '@/lib/portalAppointmentDisplay';

const PORTAL_INVOICES_RETURN = '/portal/invoices';

export function PortalDashboard() {
  const { activeClinicId, activePatientId, clinics } = usePortalActiveClinic();
  const { data: patient } = useCurrentPatient();
  const appointmentsQuery = usePortalAppointments();
  const messagesQuery = usePortalMessages(activeClinicId);
  const documentsQuery = usePortalDocuments();
  const labReportsQuery = usePortalShareableLabReports();
  const invoicesQuery = usePortalInvoices();
  const punchCardsQuery = usePortalPunchCards();
  const questionnairesQuery = usePatientFormsAssignments(activePatientId);
  const coursesQuery = usePortalTreatmentCourses();
  const bookingHref = usePortalBookingHref();
  const now = useGreetingNow();
  const clinic = clinics.find((item) => item.clinic_id === activeClinicId);
  const next = nextUpcomingAppointment(appointmentsQuery.data ?? [], now);
  const unread = (messagesQuery.data ?? []).filter(isUnreadClinicMessage);
  const pendingForms = (questionnairesQuery.data ?? []).filter(
    (row) => row.status === 'sent' || row.status === 'reminded',
  );
  const unpaid = (invoicesQuery.data ?? []).filter((row) => isCollectableInvoiceStatus(row.status));
  const todoCount = pendingForms.length + unpaid.length + (unread.length > 0 ? 1 : 0);
  const course = (coursesQuery.data ?? []).find((item) => item.status === 'active');
  const [action, setAction] = useState<{ type: 'cancel' | 'reschedule'; appointment: PortalAppointmentItem } | null>(
    null,
  );

  return (
    <>
      <PortalPageHead
        greet
        title={greetingDa(greetingPeriod(now), patient?.first_name)}
        subtitle={formatPortalLongDate(now)}
        action={<PortalBookAppointmentButton className="d-only" />}
      />

      <div className="home-grid">
        <NextAppointment
          appointment={next}
          address={clinic ? formatClinicAddress(clinic.clinic_address) : ''}
          now={now}
          isLoading={appointmentsQuery.isLoading}
          onReschedule={(appointment) => setAction({ type: 'reschedule', appointment })}
          onCancel={(appointment) => setAction({ type: 'cancel', appointment })}
        />
        <section className="card" aria-labelledby="td">
          <div className="card-h">
            <h2 id="td">Det skal du gøre</h2>
            <span className="count">{todoCount}</span>
          </div>
          <div style={{ marginTop: 8 }}>
            {todoCount === 0 ? (
              <p className="help">Du har ikke noget, der venter.</p>
            ) : null}
            {pendingForms.map((assignment) => (
              <TodoQuestionnaire key={assignment.id} assignment={assignment} />
            ))}
            {unpaid.map((invoice) => (
              <TodoInvoice key={invoice.id} invoice={invoice} />
            ))}
            {unread[0] ? (
              <div className="todo">
                <span className="ic-tile coral">
                  <PortalIcon name="message" />
                </span>
                <div className="grow">
                  <div className="t">
                    {unread.length} ulæst{unread.length === 1 ? ' besked' : 'e beskeder'}
                    {unread[0].sender_name ? ` fra ${unread[0].sender_name}` : ''}
                  </div>
                  <div className="s">
                    “{unread[0].content.slice(0, 80)}” · {formatPortalShortDate(unread[0].created_at)}
                  </div>
                </div>
                <Link className="btn btn-secondary" to="/portal/messages">
                  Læs
                </Link>
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <div className="shortcuts">
        {bookingHref ? (
          <a href={bookingHref} className="sc">
            <span className="ic-tile">
              <PortalIcon name="calendarPlus" />
            </span>
            <div>
              <b>Book ny tid</b>
              <span>Vælg tid hos klinikken</span>
            </div>
            <PortalIcon name="chevronRight" size="sm" />
          </a>
        ) : null}
        <Link to="/portal/messages" className="sc">
          <span className="ic-tile">
            <PortalIcon name="send" />
          </span>
          <div>
            <b>Skriv til klinikken</b>
            <span>Beskeder og filer</span>
          </div>
          <PortalIcon name="chevronRight" size="sm" />
        </Link>
        <Link to="/portal/documents" className="sc">
          <span className="ic-tile">
            <PortalIcon name="folder" />
          </span>
          <div>
            <b>Dokumenter</b>
            <span>
              {(documentsQuery.data ?? []).length} delt med dig
            </span>
          </div>
          <PortalIcon name="chevronRight" size="sm" />
        </Link>
        <Link to="/portal/lab-results" className="sc">
          <span className="ic-tile">
            <PortalIcon name="clipboard" />
          </span>
          <div>
            <b>Labsvar</b>
            <span>
              {(labReportsQuery.data ?? []).length} delt med dig
            </span>
          </div>
          <PortalIcon name="chevronRight" size="sm" />
        </Link>
        <Link to="/portal/punch-cards" className="sc">
          <span className="ic-tile">
            <PortalIcon name="ticket" />
          </span>
          <div>
            <b>Klippekort</b>
            <span>
              {(punchCardsQuery.data ?? []).some((card) => card.status === 'active')
                ? 'Se eller køb klip'
                : 'Se eller køb klip'}
            </span>
          </div>
          <PortalIcon name="chevronRight" size="sm" />
        </Link>
      </div>

      {course ? (
        <section className="card" style={{ marginTop: 24 }}>
          <div className="card-h">
            <h2>{course.template_name}</h2>
            <span className="chip chip-teal">Aktiv</span>
            <div className="r">
              <Link to={`/portal/treatment-courses/${course.id}`} className="link" style={{ fontSize: 14 }}>
                Se forløb
              </Link>
            </div>
          </div>
          <CourseBar
            steps={course.steps.map((step) => ({
              id: step.id,
              sort_order: step.sort_order,
              status: step.status,
            }))}
          />
        </section>
      ) : null}

      {action?.type === 'cancel' ? (
        <PortalCancelModal appointment={action.appointment} onClose={() => setAction(null)} />
      ) : null}
      {action?.type === 'reschedule' ? (
        <PortalRescheduleModal appointment={action.appointment} onClose={() => setAction(null)} />
      ) : null}
    </>
  );
}

function CourseBar({
  steps,
}: {
  steps: Array<{ id: string; sort_order: number; status: string }>;
}) {
  const progress = portalCourseForloebProgress(steps);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
      <div className="segbar" style={{ flex: 1, maxWidth: 420 }}>
        {steps.map((step, index) => (
          <i
            key={index}
            className={step.status === 'completed' ? 'd' : index === progress.n - 1 ? 'n' : undefined}
          />
        ))}
      </div>
      <b style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
        {progress.completed} af {progress.total} trin gennemført
      </b>
    </div>
  );
}

function NextAppointment({
  appointment,
  address,
  now,
  isLoading,
  onReschedule,
  onCancel,
}: {
  appointment: PortalAppointmentItem | null;
  address: string;
  now: Date;
  isLoading: boolean;
  onReschedule: (appointment: PortalAppointmentItem) => void;
  onCancel: (appointment: PortalAppointmentItem) => void;
}) {
  if (isLoading) {
    return (
      <section className="card next" aria-labelledby="na">
        <div className="band">
          <PortalIcon name="calendar" size="sm" />
          <span id="na">Næste aftale</span>
        </div>
        <div className="body">
          <p className="help">Henter din næste tid…</p>
        </div>
      </section>
    );
  }
  if (!appointment) {
    return (
      <section className="card next" aria-labelledby="na">
        <div className="band">
          <PortalIcon name="calendar" size="sm" />
          <span id="na">Næste aftale</span>
        </div>
        <div className="body">
          <div className="what">Du har ingen kommende tider.</div>
          <div className="acts">
            <PortalBookAppointmentButton />
          </div>
        </div>
      </section>
    );
  }

  const pay = paymentChip(appointment);
  return (
    <section className="card next" aria-labelledby="na">
      <div className="band">
        <PortalIcon name="calendar" size="sm" />
        <span id="na">Næste aftale</span>
        <span className="r">{formatRelativeDays(now, appointment.starts_at)}</span>
      </div>
      <div className="body">
        <div className="when num">{formatPortalDateTime(appointment.starts_at)}</div>
        <div className="what">{appointment.service_name ?? 'Aftale'}</div>
        <div className="meta">
          <div className="kv">
            <PortalIcon name="user" size="sm" />
            <div className="grow">
              <div className="k">Behandler</div>
              <div className="v">{portalAppointmentHostLabel(appointment)}</div>
            </div>
          </div>
          <div className="kv">
            <PortalIcon name="pin" size="sm" />
            <div className="grow">
              <div className="k">Sted</div>
              <div className="v">{address || appointment.clinic_name}</div>
            </div>
          </div>
          <div className="kv">
            <PortalIcon name="clock" size="sm" />
            <div className="grow">
              <div className="k">Varighed</div>
              <div className="v">{formatCourseTimeRange(appointment.starts_at, appointment.ends_at)}</div>
            </div>
          </div>
          <div className="kv">
            <PortalIcon name="wallet" size="sm" />
            <div className="grow">
              <div className="k">Betaling</div>
              <div className="v">{pay.label}</div>
            </div>
          </div>
        </div>
        <div className="acts">
          {appointment.rescheduling_allowed ? (
            <button type="button" className="btn btn-secondary" onClick={() => onReschedule(appointment)}>
              <PortalIcon name="calendarMove" size="sm" />
              Flyt tid
            </button>
          ) : null}
          {appointment.cancellation_allowed ? (
            <button type="button" className="btn btn-secondary" onClick={() => onCancel(appointment)}>
              <PortalIcon name="calendarX" size="sm" />
              Aflys
            </button>
          ) : null}
          <Link className="btn btn-tertiary" to="/portal/appointments">
            Se aftale
          </Link>
        </div>
        {appointmentRuleText(appointment, formatPortalDateTime(appointment.cancel_until)) &&
        (appointment.cancellation_allowed || appointment.rescheduling_allowed) ? (
          <p className="rule">
            <PortalIcon name="info" size="xs" />
            Du kan {appointment.rescheduling_allowed ? 'flytte eller ' : ''}aflyse uden gebyr indtil{' '}
            {formatPortalDateTime(appointment.cancel_until)}.
          </p>
        ) : isPortalPaidLocked(appointment) ? (
          <p className="rule">
            <PortalIcon name="info" size="xs" />
            Betalte tider ændres via klinikken.{' '}
            <Link className="link" to="/portal/messages">
              Skriv til klinikken
            </Link>
            .
          </p>
        ) : null}
      </div>
    </section>
  );
}

function isPortalPaidLocked(appointment: PortalAppointmentItem): boolean {
  return appointment.display_status === 'upcoming' && !appointment.rescheduling_allowed;
}

function TodoQuestionnaire({ assignment }: { assignment: PatientFormsAssignment }) {
  const { mutate: requestLink, isPending } = useRequestPortalFormLink();
  const { map } = useQuestionnaireNameMap(assignment.clinic_id, 'da');
  const title = assignmentQuestionnaireName(assignment, map, 'da', 'Spørgeskema');
  const cached = publicUrlFromAssignmentMetadata(assignment.metadata);

  const open = () => {
    if (cached) {
      window.open(cached, '_blank', 'noopener,noreferrer');
      return;
    }
    requestLink(assignment.id, {
      onSuccess(result) {
        if (result.ok && result.url) window.open(result.url, '_blank', 'noopener,noreferrer');
      },
    });
  };

  return (
    <div className="todo">
      <span className="ic-tile amber">
        <PortalIcon name="clipboard" />
      </span>
      <div className="grow">
        <div className="t">Udfyld {title}</div>
        <div className="s">
          {assignment.due_at ? `Svarfrist ${formatPortalDateTime(assignment.due_at)}` : 'Afventer svar'}
        </div>
      </div>
      <button type="button" className="btn btn-primary" onClick={open} disabled={isPending}>
        Udfyld
      </button>
    </div>
  );
}

function TodoInvoice({
  invoice,
}: {
  invoice: { id: string; invoice_number: string | null; total_cents: number; due_at: string | null; pay_token: string | null };
}) {
  const createCheckout = useCreatePayCheckout();
  const handlePay = async () => {
    const result = await createCheckout.mutateAsync({
      ...(invoice.pay_token ? { pay_token: invoice.pay_token } : { invoice_id: invoice.id }),
      ...buildPayCheckoutReturnUrls(PORTAL_INVOICES_RETURN),
    });
    if (result.pay_token) {
      sessionStorage.setItem(payReturnStorageKey(result.pay_token), PORTAL_INVOICES_RETURN);
      markPortalPaymentPending({ payToken: result.pay_token });
    }
    window.location.href = result.checkout_url;
  };
  return (
    <div className="todo">
      <span className="ic-tile">
        <PortalIcon name="invoice" />
      </span>
      <div className="grow">
        <div className="t">
          Faktura {invoice.invoice_number ?? invoice.id.slice(0, 8)} · {formatPortalKroner(invoice.total_cents)}
        </div>
        <div className="s">
          Ubetalt
          {invoice.due_at ? ` · forfald ${formatPortalDateTime(invoice.due_at)}` : ''}
        </div>
      </div>
      <button type="button" className="btn btn-secondary" onClick={() => void handlePay()} disabled={createCheckout.isPending}>
        Betal
      </button>
    </div>
  );
}
